import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { Helmet } from 'react-helmet';
import { graphql } from 'gatsby';
import { DiscussionEmbed } from 'disqus-react';
import moment from 'moment';
import { Inline } from 'chaoskit/src/components';

import { config } from '../../helpers/config';
import BaseLayout from '../../layouts/BaseLayout';
import { Icon, Link } from '../../components';
import { createFullIndexUrl } from '../../../utils/helpers';
import Author from '../../partials/global/Author';
import ArticleCTA from '../../partials/global/ArticleCTA';
import TutorialTeaser from '../../partials/community/tutorials/TutorialTeaser';
import SocialGroup from '../../partials/global/SocialGroup';

const Tutorial = ({
  data: {
    tutorial: {
      headings,
      tableOfContents,
      html,
      excerpt,
      frontmatter: {
        title,
        description,
        dateModified,
        author,
        categories,
        socialMediaImage,
        articleCTA,
      },
      fields: { fullUrl, authorUrl, categoryUrls, featuredImage },
    },
    config: {
      siteMetadata: { siteUrl },
    },
    related,
  },
  location,
  pageContext: { nodeType },
}) => {
  // Workaround for Gatsby's location prop not reliably being available on server-loads
  const currentPageUrl = `${siteUrl}/${fullUrl}`;

  const renderCategories = (category, idx) => (
    <Fragment key={idx}>
      {!!idx && ', '}
      <Link className="u-linkDefault u-linkUnderline" to={category.slug}>
        {category.name}
      </Link>
    </Fragment>
  );

  // Get list of categories with URLs
  const categoryData = [];
  if (categories) {
    categories.forEach((category, index) => {
      categoryData.push({
        name: category,
        slug: categoryUrls[index],
      });
    });
  }

  const tutorialRowClasses = cx('row row--largeGutter', {
    'u-flexCenter': !headings.length,
  });
  const disqusConfig = {
    title,
    url: currentPageUrl,
    identifier: location.pathname.replace(/^\/|\/$/g, ''), // Matching what was on old site to preserve comments
  };

  return (
    <BaseLayout headerProps={{ contrast: false, community: true }}>
      <Helmet
        title={title}
        meta={[
          { property: 'og:image', content: socialMediaImage },
          { name: 'description', content: description || excerpt },
          { name: 'node-type', content: nodeType },
        ]}
      />
      <div className="static-wrapper">
        <aside className="tutorial__breadcrumb u-textSmall as-stickyTrigger">
          <div className="container">
            <Inline>
              <Link
                to={createFullIndexUrl('tutorial', '', false)}
                className="u-linkUnderline u-linkDefault"
              >
                Tutorials
              </Link>
              {categories && (
                <Fragment>
                  <Icon
                    size="small"
                    className="u-textMuted"
                    icon="caret-right"
                  />
                  <div className="tutorial__breadcrumb-categories">
                    {categoryData.map(renderCategories)}
                  </div>
                </Fragment>
              )}
            </Inline>
          </div>
        </aside>
        <section className="section">
          <div className="container">
            <div className={tutorialRowClasses}>
              {headings.length ? (
                <div className="column-3@medium u-hiddenDown@small">
                  <aside
                    className="site__toc"
                    dangerouslySetInnerHTML={{ __html: tableOfContents }}
                  />
                </div>
              ) : null}
              <div className="column-9@medium">
                <div className="tutorial__main">
                  <header className="tutorial__header">
                    <h1>{title}</h1>
                    <div className="tutorial__hero">
                      <img src={featuredImage} alt={title} />
                    </div>
                    <div className="tutorial__meta">
                      <Author name={author} url={authorUrl} />
                      <div className="tutorial__social">
                        <SocialGroup
                          networks={[
                            {
                              service: 'twitter',
                              url: `https://twitter.com/intent/tweet?text=${title}&url=${currentPageUrl}`,
                              title: 'Share on Twitter',
                            },
                            {
                              service: 'facebook',
                              url: `https://www.facebook.com/sharer.php?u=${currentPageUrl}`,
                              title: 'Share on Facebook',
                            },
                            {
                              service: 'linkedin',
                              url: `https://www.linkedin.com/shareArticle?title=${title}&url=${currentPageUrl}`,
                              title: 'Share on LinkedIn',
                            },
                            {
                              service: 'hacker-news',
                              url: `http://news.ycombinator.com/submitlink?u=${currentPageUrl}&t=${title}`,
                              title: 'Share on Hacker News',
                            },
                          ]}
                        />
                      </div>
                    </div>
                  </header>
                  <main
                    className="tutorial__content has-anchorBump"
                    dangerouslySetInnerHTML={{ __html: html }}
                  />
                </div>
              </div>
            </div>
            <footer className="tutorial__footer">
              <div className="tutorial__footer-date">
                <strong>Last Updated:</strong>{' '}
                {moment(dateModified).format('LL')}
              </div>
              {categories && (
                <div className="tutorial__footer-categories">
                  <strong>Categories:</strong>{' '}
                  {categoryData.map(renderCategories)}
                </div>
              )}
            </footer>
            <ArticleCTA cta={articleCTA} type="tutorial" />
            <div className="tutorial__comments">
              <DiscussionEmbed
                shortname={config.disqusShortname}
                config={disqusConfig}
              />
            </div>
            {related && related.edges.length > 0 && (
              <div className="tutorial__related">
                <h3 className="u-textFluid--h3-h4">Related</h3>
                <ul className="blockGrid blockGrid-3@medium blockGrid--deepMatch">
                  {related.edges.map(({ node }) => (
                    <li key={node.fileAbsolutePath}>
                      <TutorialTeaser
                        title={node.frontmatter.title}
                        link={node.fields.fullUrl}
                        excerpt={node.excerpt}
                        categories={node.frontmatter.categories}
                        author={node.frontmatter.author}
                        authorUrl={node.fields.authorUrl}
                      />
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </section>
      </div>
    </BaseLayout>
  );
};

Tutorial.propTypes = {
  data: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired,
  pageContext: PropTypes.object.isRequired,
};

export default Tutorial;

export const pageQuery = graphql`
  query TutorialSingle(
    $slug: String!
    $categories: [String]
    $fileAbsolutePath: String!
  ) {
    tutorial: markdownRemark(
      fields: { slug: { eq: $slug } }
      fileAbsolutePath: { regex: "/src/tutorials/" }
    ) {
      html
      excerpt
      headings {
        value
        depth
      }
      tableOfContents(pathToSlugField: "fields.fullUrl", maxDepth: 3)
      timeToRead
      frontmatter {
        title
        description
        date
        dateModified
        slug
        categories
        author
        socialMediaImage
        articleCTA
      }
      fields {
        slug
        fullUrl
        categoryUrls
        authorUrl
        featuredImage
      }
    }

    config: site {
      siteMetadata {
        siteUrl
      }
    }

    related: allMarkdownRemark(
      limit: 3
      filter: {
        frontmatter: { categories: { in: $categories } }
        # Make sure we're only querying tutorials
        # Filter out current tutorial
        fileAbsolutePath: { regex: "/src/tutorials/", ne: $fileAbsolutePath }
      }
    ) {
      edges {
        node {
          ...TutorialTeaserFragment
        }
      }
    }
  }
`;
